import { Breadcrumb, Card } from "react-bootstrap";
import { Link, useLocation, useParams } from "react-router";

import {
    useAppDocument,
    useDockDocument,
    useFlightDocument,
    useLocationDocument,
    useMissionDocument,
    useOrganizationDocument,
    usePayloadDocument,
    useRoutineDocument,
    useTaskDocument,
} from "../../api/firestoreWrappers.generated.jsx";
import { useUser } from "../../contexts/user_provider";
import type {
    BreadcrumbData,
    BreadcrumbDataKey,
    BreadcrumbPath,
    BreadcrumbPathData,
} from "./breadcrumbItems";

type BreadcrumbsProps<Path extends BreadcrumbPath<BreadcrumbDataKey>> = {
    path: Path;
} & NoInfer<BreadcrumbPathData<Path>>;

export function Breadcrumbs<Path extends BreadcrumbPath<BreadcrumbDataKey>>(
    props: BreadcrumbsProps<Path>,
) {
    const { path } = props;
    const suppliedData = props as BreadcrumbData;
    const params = useParams();
    const routerLocation = useLocation();
    const { getUserIsAdmin } = useUser();
    const isAdmin = getUserIsAdmin();
    const needsData = (key: BreadcrumbDataKey) => path.dataKeys.includes(key);
    const query = new URLSearchParams(routerLocation.search);
    const missionId = params.missionId ?? query.get("missionId") ?? undefined;
    const flightId = params.flightId ?? query.get("flightId") ?? undefined;

    const fetchOrganization =
        needsData("organization") &&
        isAdmin &&
        suppliedData.organization === undefined;
    const fetchLocation =
        needsData("location") && suppliedData.location === undefined;
    const fetchDock = needsData("dock") && suppliedData.dock === undefined;
    const fetchTask = needsData("task") && suppliedData.task === undefined;
    const fetchPayload =
        needsData("payload") && suppliedData.payload === undefined;
    const fetchRoutine =
        needsData("routine") && suppliedData.routine === undefined;
    const fetchApp = needsData("app") && suppliedData.app === undefined;
    const fetchMission =
        needsData("mission") && suppliedData.mission === undefined;
    const fetchFlight =
        needsData("flight") && suppliedData.flight === undefined;

    const [fetchedOrganization] = useOrganizationDocument(
        fetchOrganization ? params.organizationId : undefined,
        { mode: "once" },
    ) as [BreadcrumbData["organization"]];
    const [fetchedLocation] = useLocationDocument(
        fetchLocation ? params.organizationId : undefined,
        fetchLocation ? params.locationId : undefined,
        { mode: "once" },
    ) as [BreadcrumbData["location"]];
    const [fetchedDock] = useDockDocument(
        fetchDock ? params.organizationId : undefined,
        fetchDock ? params.locationId : undefined,
        fetchDock ? params.dockId : undefined,
        { mode: "once" },
    ) as [BreadcrumbData["dock"]];
    const [fetchedTask] = useTaskDocument(
        fetchTask ? params.organizationId : undefined,
        fetchTask ? params.locationId : undefined,
        fetchTask ? params.taskId : undefined,
        { mode: "once" },
    ) as [BreadcrumbData["task"]];
    const [fetchedPayload] = usePayloadDocument(
        fetchPayload ? params.payloadId : undefined,
        { mode: "once" },
    ) as [BreadcrumbData["payload"]];
    const [fetchedRoutine] = useRoutineDocument(
        fetchRoutine ? params.organizationId : undefined,
        fetchRoutine ? params.locationId : undefined,
        fetchRoutine ? params.routineId : undefined,
        { mode: "once" },
    ) as [BreadcrumbData["routine"]];
    const [fetchedApp] = useAppDocument(fetchApp ? params.appId : undefined, {
        mode: "once",
    }) as [BreadcrumbData["app"]];
    const [fetchedMission] = useMissionDocument(
        fetchMission ? params.organizationId : undefined,
        fetchMission ? params.locationId : undefined,
        fetchMission ? missionId : undefined,
        { mode: "once" },
    ) as [BreadcrumbData["mission"]];
    const [fetchedFlight] = useFlightDocument(
        fetchFlight ? params.organizationId : undefined,
        fetchFlight ? params.locationId : undefined,
        fetchFlight ? flightId : undefined,
        { mode: "once" },
    ) as [BreadcrumbData["flight"]];

    const context = {
        organization: suppliedData.organization ?? fetchedOrganization,
        location: suppliedData.location ?? fetchedLocation,
        dock: suppliedData.dock ?? fetchedDock,
        payload: suppliedData.payload ?? fetchedPayload,
        routine: suppliedData.routine ?? fetchedRoutine,
        task: suppliedData.task ?? fetchedTask,
        app: suppliedData.app ?? fetchedApp,
        mission: suppliedData.mission ?? fetchedMission,
        flight: suppliedData.flight ?? fetchedFlight,
        isAdmin,
        params,
        query,
    };
    const items = path.items.filter((item) => item.visible?.(context) ?? true);

    return (
        <Card className="mb-4">
            <Card.Body className="px-4 py-2">
                <Breadcrumb>
                    {items.map((item, index) => {
                        const active = index === items.length - 1;
                        const to = active ? undefined : item.to?.(context);

                        return (
                            <Breadcrumb.Item
                                // biome-ignore lint/suspicious/noArrayIndexKey: Breadcrumb items are stateless entries in a predefined ordered path and have no stable identifier.
                                key={index}
                                active={active}
                                linkAs={to ? Link : "span"}
                                linkProps={
                                    to
                                        ? {
                                              className: "text-decoration-none",
                                              to,
                                          }
                                        : undefined
                                }
                            >
                                {item.label(context)}
                            </Breadcrumb.Item>
                        );
                    })}
                </Breadcrumb>
            </Card.Body>
        </Card>
    );
}
